import React, { useContext, useMemo } from 'react'
import { OneXTwoBetPick, TOneXTwoBet } from './types'
import WithOneXTwoBet, { IOneXTwoContext, OneXTwoContext } from './WithOneXTwoBet'
import DisplaySaveResult from '@root/bet_utils/DisplaySaveResult'
import { AwayPickButton, DrawPickButton, HomePickButton } from './PickButton'
import I18n from 'i18n-js'
import BetStatus from '@root/bet_utils/BetStatus'
import { EmptyPick, isEmptyPick } from '@root/bet_utils/types'
import NextBetTeaser from './NextBetTeaser'
import { BettingOddsPartner } from '@root/misc/bettingOddsPartner/type'
import { BettingOddsContext, IBettingOddsContext } from '@root/misc/bettingOddsPartner/BettingOddsContext'
import useBettingOddsPartner from '@root/misc/bettingOddsPartner/useBettingOddsPartner'

const BetResultInfo: React.FC<{ownPick: EmptyPick | OneXTwoBetPick, actualResult: TOneXTwoBet['actual_result'], teams: TOneXTwoBet['teams'], justTemporary?: boolean}> = ({ ownPick, actualResult, teams, justTemporary }) => {
  const madePick = !isEmptyPick(ownPick);
  const madeCorrectPick = madePick && ownPick === actualResult;

  const pillClass = [
    'oneXtwo__correctnessPill',
    madePick
      ? (
          madeCorrectPick
            ? 'oneXtwo__correctnessPill--pickedCorrect'
            : 'oneXtwo__correctnessPill--pickedWrong'
        )
      : 'oneXtwo__correctnessPill--pickedNothing'
  ].join(" ")

  const pillLabel = madePick
    ? (
        madeCorrectPick
          ? I18n.t('onextwo.result_picked_correct')
          : I18n.t('onextwo.result_picked_wrong')
      )
    : I18n.t('onextwo.result_picked_nothing')

  return <div className="oneXtwo__resultInfo">
    <span>{actualResult === 'draw'
      ? I18n.t(`onextwo.result_draw${justTemporary ? '--temp' : ''}`)
      : I18n.t(`onextwo.result_team_won${justTemporary ? '--temp' : ''}`, { team: actualResult === 'home' ? teams?.home?.label || '' : teams?.away?.label || ''})}</span>
    <span className={pillClass}>{pillLabel}</span>
  </div>
}

export const RawOneXTwoBet: React.FC = () => {
  const { bet, changePicks, displaySaveResult } = useContext(OneXTwoContext)
  const { BettingOddsComp } = useBettingOddsPartner()

  return <div className="oneXtwo">
    <div className="oneXtwo__meta">
      <h4>{bet.event_name}</h4>
      <div><BetStatus /></div>
    </div>
    <div className="oneXtwo__box">
      <div className="oneXtwo__content">
        <div className="oneXtwo__picks">
          <HomePickButton />
          {bet.pick_mode === 'home_or_away_or_draw' &&
            <DrawPickButton />}
          <AwayPickButton />
        </div>
        {bet.actual_result &&
          <BetResultInfo justTemporary={bet.scoring_state === "temp"} actualResult={bet.actual_result} ownPick={bet.picks?.[0]} teams={bet.teams} />}
      </div>
      {(bet.cancelled || bet.postponed)
        && <div className="oneXtwo__cancelledInfo">
            {bet.postponed && I18n.t('postponed.info')}
            {bet.cancelled && I18n.t('cancelled.info')}
          </div>}
      <DisplaySaveResult classPrefix='oneXtwo' saveResult={displaySaveResult} />
      <div className="oneXtwo__quotesHolder">
        {BettingOddsComp}
      </div>
    </div>
  </div>
}

interface OneXTwoBetProps {
  bet: TOneXTwoBet
  authToken: string
  isAdult: boolean
  oddsPartner?: BettingOddsPartner
}

const OneXTwoBet: React.FC<OneXTwoBetProps> = ({bet, authToken, isAdult, oddsPartner}) => {
  const oddsContextVal = useMemo<IBettingOddsContext>(() => ({
    isAdult,
    partner: oddsPartner
  }), [isAdult, oddsPartner])

  return <WithOneXTwoBet initialBet={bet} authToken={authToken}>
    <BettingOddsContext.Provider value={oddsContextVal}>
      <RawOneXTwoBet />
    </BettingOddsContext.Provider>
  </WithOneXTwoBet>
}

export { NextBetTeaser }

export default OneXTwoBet
